// components/Home/ContactUs.tsx
"use client";

import axios from "axios";
import { FormEvent, useState } from "react";
import toast from "react-hot-toast";
import { LuClock } from "react-icons/lu";

const API_BASE =
  process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:3001";

/* ===================== Types ===================== */
export default function ContactUs() {
  const [form, setForm] = useState({
    userName: "",
    userEmail: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/contact-us`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ userName: "", userEmail: "", subject: "", message: "" });
    } catch (error: unknown) {
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? Array.isArray(error.response.data.message)
            ? error.response.data.message[0]
            : error.response.data.message
          : "Failed to send message. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-32 pb-20 px-6 max-w-7xl mx-auto min-h-screen">
      <div className="grid lg:grid-cols-2 gap-16 items-start">
        {/* Hero Content Section */}
        <section className="space-y-8">
          <div className="inline-block px-3 py-1 rounded-full bg-black/5 dark:bg-[#2a2a2a] border border-black/10 dark:border-[#464555]/20">
            <span className="text-xs font-bold tracking-widest uppercase text-[#c1c1ff]">
              Get In Touch
            </span>
          </div>
          <h1 className="text-6xl md:text-7xl font-extrabold tracking-tighter text-black dark:text-[#e2e2e2] leading-[0.9]">
            Need help?
          </h1>
          <p className="text-xl md:text-2xl text-zinc-600 dark:text-[#c7c4d8] leading-relaxed max-w-xl font-light">
            If something is unclear, missing, or confusing, tell us. We’re
            building Lattu for students like you, and your feedback matters.
          </p>

          {/* Decorative Element: Luminary Track */}
          <div className="relative pt-12">
            <div className="absolute left-0 top-0 w-32 h-1 bg-linear-to-r from-[#615fff] to-transparent rounded-full shadow-[0_0_15px_rgba(97,95,255,0.5)]"></div>
            {/* <div className="flex flex-col space-y-6">
              <div className="flex items-center space-x-4 group">
                <div className="p-3 rounded-xl bg-black/5 dark:bg-[#2a2a2a] text-[#c1c1ff] group-hover:bg-[#615fff] group-hover:text-white transition-all duration-300">
                  <MdAlternateEmail size={24} />
                </div>
                <div>
                  <p className="text-sm text-zinc-500 dark:text-[#908fa1] font-medium">Email us directly</p>
                  <p className="text-lg font-bold text-black dark:text-[#e2e2e2]">hello@lattu.ai</p>
                </div>
              </div>
              <div className="flex items-center space-x-4 group">
                <div className="p-3 rounded-xl bg-black/5 dark:bg-[#2a2a2a] text-[#c1c1ff] group-hover:bg-[#615fff] group-hover:text-white transition-all duration-300">
                  <FaDiscord size={24} />
                </div>
                <div>
                  <p className="text-sm text-zinc-500 dark:text-[#908fa1] font-medium">Community</p>
                  <p className="text-lg font-bold text-black dark:text-[#e2e2e2]">Join our Discord</p>
                </div>
              </div>
            </div> */}
          </div>
        </section>

        {/* Form Section */}
        <section className="relative">
          {/* Background Luminescence */}
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#615fff]/10 blur-[120px] rounded-full pointer-events-none"></div>

          <div className="bg-white dark:bg-[#1f1f1f] p-8 md:p-12 rounded-4xl shadow-2xl relative border border-black/10 dark:border-[#464555]/10">
            <form onSubmit={onSubmit} className="space-y-6">
              <div className="space-y-2">
                <label
                  className="text-xs font-bold uppercase tracking-widest text-zinc-500 dark:text-[#908fa1] ml-1"
                  htmlFor="userName"
                >
                  Full Name
                </label>
                <input
                  className="w-full bg-zinc-100 dark:bg-[#353535] border-none rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#c1c1ff]/30 text-black dark:text-[#e2e2e2] transition-all placeholder:text-zinc-400 dark:placeholder:text-[#908fa1]/50 outline-none"
                  id="userName"
                  name="userName"
                  value={form.userName}
                  onChange={handleChange}
                  placeholder="Your name"
                  required
                  type="text"
                />
              </div>

              <div className="space-y-2">
                <label
                  className="text-xs font-bold uppercase tracking-widest text-zinc-500 dark:text-[#908fa1] ml-1"
                  htmlFor="userEmail"
                >
                  Email Address
                </label>
                <input
                  className="w-full bg-zinc-100 dark:bg-[#353535] border-none rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#c1c1ff]/30 text-black dark:text-[#e2e2e2] transition-all placeholder:text-zinc-400 dark:placeholder:text-[#908fa1]/50 outline-none"
                  id="userEmail"
                  name="userEmail"
                  value={form.userEmail}
                  onChange={handleChange}
                  placeholder="name@university.edu"
                  required
                  type="email"
                />
              </div>

              <div className="space-y-2">
                <label
                  className="text-xs font-bold uppercase tracking-widest text-zinc-500 dark:text-[#908fa1] ml-1"
                  htmlFor="subject"
                >
                  Subject
                </label>
                <input
                  className="w-full bg-zinc-100 dark:bg-[#353535] border-none rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#c1c1ff]/30 text-black dark:text-[#e2e2e2] transition-all placeholder:text-zinc-400 dark:placeholder:text-[#908fa1]/50 outline-none"
                  id="subject"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="Title"
                  required
                  type="text"
                />
              </div>

              <div className="space-y-2">
                <label
                  className="text-xs font-bold uppercase tracking-widest text-zinc-500 dark:text-[#908fa1] ml-1"
                  htmlFor="message"
                >
                  Message
                </label>
                <textarea
                  className="w-full bg-zinc-100 dark:bg-[#353535] border-none rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#c1c1ff]/30 text-black dark:text-[#e2e2e2] transition-all placeholder:text-zinc-400 dark:placeholder:text-[#908fa1]/50 resize-none outline-none"
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us how we can help..."
                  required
                  rows={5}
                ></textarea>
              </div>

              <div className="pt-4">
                <button
                  className="w-full bg-[#615fff] text-[#fffeff] font-bold py-5 rounded-xl text-lg hover:bg-[#4F39F6] transform transition-all active:scale-[0.98] shadow-lg shadow-[#615fff]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send Message"}
                </button>
                <div className="mt-6 flex items-center justify-center space-x-2 text-zinc-500 dark:text-[#908fa1]/60 italic text-sm">
                  <LuClock size={16} />
                  <span>We usually reply within 24 - 48 hours.</span>
                </div>
              </div>
            </form>
          </div>
        </section>
      </div>
    </main>
  );
}
